import type { SongSpec } from "../audio/AudioEngine";
import type { Direction } from "../core/Input";
import type { TileSet } from "../world/Tiles";
import type { MapDef, NpcDef } from "../world/TileMap";
import type { BattleOutcome } from "./Battle";
import type { HeroClassDef } from "./Hero";
import type { MonsterDef } from "./Monster";
import type { Party } from "./Party";
import { Registry, type ItemDef, type SkillDef } from "./Skills";

export interface InteractionContext {
  world: World;
  /** Runs a dialogue page and waits for the player to dismiss it. */
  say: (text: string) => Promise<void>;
  /** Opens a yes/no prompt. */
  ask: (question: string) => Promise<boolean>;
  /** Opens an arbitrary menu of labels; resolves with the index or null. */
  choose: (title: string, options: string[]) => Promise<number | null>;
  /** Starts a scripted battle against the given monster ids. */
  fight: (monsters: string[]) => Promise<BattleOutcome>;
  /** Rebuilds the map — call after changing the party roster. */
  reload: () => void;
}

/** Return true to suppress the NPC's default dialogue. */
export type InteractionHandler = (npc: NpcDef, ctx: InteractionContext) => Promise<boolean>;

export interface WorldOptions {
  tileset: TileSet;
  maps: readonly MapDef[];
  monsters: readonly MonsterDef[];
  skills: readonly SkillDef[];
  items: readonly ItemDef[];
  classes: readonly HeroClassDef[];
  party: Party;
  startMap: string;
  /** Where the party wakes up after a defeat. Defaults to the start map spawn. */
  respawn?: { map: string; x: number; y: number };
  songs?: Record<string, SongSpec>;
  /** Game-specific behaviour for NPCs, signs, shops, inns. */
  onInteract?: InteractionHandler;
}

/**
 * Everything the game is made of, in one place: content registries, the party,
 * and where they currently are. Scenes read and mutate this, so travelling
 * between maps is just swapping `location`.
 */
export class World {
  readonly tileset: TileSet;
  readonly maps: Registry<MapDef>;
  readonly monsters: Registry<MonsterDef>;
  readonly skills: Registry<SkillDef>;
  readonly items: Registry<ItemDef>;
  readonly classes: Registry<HeroClassDef>;
  readonly party: Party;
  readonly songs: Record<string, SongSpec>;
  readonly onInteract: InteractionHandler | undefined;

  location: { map: string; x: number; y: number; facing: Direction };
  readonly respawn: { map: string; x: number; y: number };

  constructor(options: WorldOptions) {
    this.tileset = options.tileset;
    this.maps = new Registry("map", options.maps);
    this.monsters = new Registry("monster", options.monsters);
    this.skills = new Registry("skill", options.skills);
    this.items = new Registry("item", options.items);
    this.classes = new Registry("class", options.classes);
    this.party = options.party;
    this.songs = options.songs ?? {};
    this.onInteract = options.onInteract;

    const start = this.maps.get(options.startMap);
    this.location = {
      map: start.id,
      x: start.spawn?.x ?? 1,
      y: start.spawn?.y ?? 1,
      facing: start.spawn?.facing ?? "down",
    };
    this.respawn = options.respawn ?? { map: this.location.map, x: this.location.x, y: this.location.y };
  }

  get currentMap(): MapDef {
    return this.maps.get(this.location.map);
  }

  /** Places the party on a map. The field scene grants the safe distance. */
  moveTo(mapId: string, x: number, y: number, facing?: Direction): void {
    const map = this.maps.get(mapId);
    this.location = { map: map.id, x, y, facing: facing ?? this.location.facing };
  }
}
